<template>
  <div class="page-root">
    <!-- Scroll progress bar -->
    <div class="scroll-progress" :style="{ transform: `scaleX(${scrollProgress})` }"></div>

    <!-- ============================ HERO ============================ -->
    <section ref="heroRef" class="hero">
      <!-- Aurora / parallax background layers -->
      <div class="hero-bg" aria-hidden="true">
        <div class="aurora aurora-1" data-mdepth="18"></div>
        <div class="aurora aurora-2" data-mdepth="30"></div>
        <div class="aurora aurora-3" data-mdepth="12"></div>
        <div class="starfield starfield-near" data-mdepth="40"></div>
        <div class="starfield starfield-far" data-mdepth="14"></div>
        <div class="grid-floor"></div>

        <!-- Floating logistics glyphs -->
        <div class="glyph glyph-1 float-a" data-mdepth="55"><i class="fas fa-box"></i></div>
        <div class="glyph glyph-2 float-b" data-mdepth="35"><i class="fas fa-truck"></i></div>
        <div class="glyph glyph-3 float-c" data-mdepth="70"><i class="fas fa-paper-plane"></i></div>
        <div class="glyph glyph-4 float-b" data-mdepth="25"><i class="fas fa-map-marker-alt"></i></div>
        <div class="glyph glyph-5 float-a" data-mdepth="48"><i class="fas fa-rupee-sign"></i></div>

        <!-- Cargo plane flyover -->
        <div class="hero-plane">
          <span class="hero-plane-trail"></span>
          <i class="fas fa-plane"></i>
        </div>
      </div>

      <div class="hero-inner">
        <!-- Left: copy -->
        <div class="hero-copy">
          <img src="/images/vamaship-logo.png" alt="Vamaship" class="hero-logo" />

          <div class="hero-kicker">
            <span class="kicker-dot"></span>
            India's smartest shipping platform
          </div>

          <h1 class="hero-h1">
            <span
              v-for="(word, i) in heroContent.h1.split(' ')"
              :key="`hw-${i}-${word}`"
              class="hero-word"
              :style="{ animationDelay: `${0.12 + i * 0.07}s` }"
            >{{ word }}&nbsp;</span>
          </h1>
          <p class="hero-sub">{{ heroContent.span }}</p>

          <div class="hero-image-wrap" data-mdepth="20">
            <img :src="heroContent.image" alt="Vamaship shipping" class="hero-image float-a" />
            <div class="hero-image-glow"></div>
          </div>

          <div class="hero-chips">
            <div class="hero-chip"><i class="fas fa-bolt"></i> Same-day COD payouts</div>
            <div class="hero-chip"><i class="fab fa-whatsapp"></i> 90% delivery success</div>
            <div class="hero-chip"><i class="fas fa-percentage"></i> Save up to 40%</div>
          </div>
        </div>

        <!-- Right: signup card -->
        <div class="hero-form-col">
          <div
            ref="tiltCardRef"
            class="form-card"
            @mousemove="handleCardTilt"
            @mouseleave="resetCardTilt"
          >
            <div class="form-card-border" aria-hidden="true"></div>
            <div class="form-card-body">
              <!-- Tabs -->
              <div v-if="!signupStore.otpSent" class="form-tabs">
                <button
                  @click="activeTab = 'seller'"
                  :class="['form-tab', activeTab === 'seller' ? 'form-tab-active' : '']"
                >
                  <i class="fas fa-store"></i>
                  <span>Are you a seller?</span>
                </button>
                <button
                  @click="activeTab = 'track'"
                  :class="['form-tab', activeTab === 'track' ? 'form-tab-active' : '']"
                >
                  <i class="fas fa-shopping-cart"></i>
                  <span>Track Your Order</span>
                </button>
              </div>

              <template v-if="activeTab === 'seller' || signupStore.otpSent">
                <h2 class="form-title">Create your Seller Account</h2>
                <p class="form-subtitle">For businesses shipping orders with Vamaship</p>
              </template>

              <!-- Seller tab -->
              <div v-if="activeTab === 'seller'">
                <!-- Phone step -->
                <div v-if="!signupStore.otpSent" class="space-y-3">
                  <div>
                    <label class="form-label">Phone Number</label>
                    <div class="relative">
                      <div
                        v-if="showTrackTooltip && !signupStore.otpSent"
                        class="track-tooltip"
                        role="tooltip"
                      >
                        <div class="flex items-center gap-2">
                          <span>
                            Looking to track your order?
                            <a
                              href="https://vamaship.co/"
                              target="_blank"
                              rel="noopener"
                              class="underline text-blue-200 hover:text-white"
                            >Track here →</a>
                          </span>
                          <button
                            type="button"
                            @click="dismissTrackTooltip"
                            class="text-white/80 hover:text-white leading-none"
                            aria-label="Close tracking tooltip"
                          >x</button>
                        </div>
                        <span class="track-tooltip-arrow"></span>
                      </div>
                      <input
                        v-model="signupStore.formData.phone"
                        @input="handlePhoneInput"
                        type="tel"
                        placeholder="Enter Phone Number"
                        class="form-input form-input--phone"
                        maxlength="10"
                      />
                      <div class="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center">
                        <span class="text-gray-600 font-medium">+91</span>
                        <span class="text-gray-400 mx-3">|</span>
                      </div>
                    </div>
                    <p class="form-hint">We'll send a verification code to this number</p>
                  </div>

                  <button
                    @click="sendOtp"
                    :disabled="signupStore.formData.phone.length !== 10 || signupStore.isLoading"
                    class="cta-button"
                    :class="{ 'cta-button-disabled': signupStore.formData.phone.length !== 10 || signupStore.isLoading }"
                  >
                    <SpinnerLoader v-if="signupStore.isLoading" size="sm" class="mr-3" />
                    <span>{{ signupStore.isLoading ? 'Sending...' : 'Send OTP' }}</span>
                    <i v-if="!signupStore.isLoading" class="fas fa-arrow-right cta-arrow"></i>
                  </button>

                  <div class="text-center mt-4">
                    <span class="text-sm text-gray-600">Already have an account? </span>
                    <button @click="goToLogin" class="link-button">Sign In</button>
                  </div>

                  <div v-if="signupStore.errors.general" class="error-box">
                    <i class="fas fa-exclamation-circle text-red-500 mr-2"></i>
                    <span>{{ signupStore.errors.general }}</span>
                  </div>
                  <div v-if="signupStore.errors.phone" class="error-box">
                    <i class="fas fa-exclamation-circle text-red-500 mr-2"></i>
                    <span>{{ signupStore.errors.phone }}</span>
                  </div>
                </div>

                <!-- OTP step -->
                <div v-if="signupStore.otpSent" class="mt-4">
                  <div class="mb-3">
                    <label class="form-label">Enter OTP</label>
                    <OtpInput v-model="signupStore.formData.otp" :length="6" @complete="verifyOtp" />
                    <div v-if="signupStore.errors.otp" class="mt-2 text-center">
                      <p class="text-xs text-red-600 font-medium">
                        <i class="fas fa-exclamation-circle mr-1"></i>
                        {{ signupStore.errors.otp }}
                      </p>
                    </div>
                    <div v-if="signupStore.otpCooldown > 0" class="mt-2 text-center">
                      <p class="text-xs text-gray-600">
                        Resend OTP in
                        <span class="font-mono text-blue-600 font-semibold">{{ formatOtpTime(signupStore.otpCooldown) }}</span>
                      </p>
                    </div>
                    <div v-if="signupStore.otpCooldown === 0" class="mt-2 text-center">
                      <button @click="resendOtp" class="link-button text-xs">Resend OTP</button>
                    </div>
                  </div>

                  <button
                    @click="verifyOtp"
                    :disabled="!signupStore.isOtpComplete"
                    class="cta-button"
                    :class="{ 'cta-button-disabled': !signupStore.isOtpComplete }"
                  >
                    <span>Verify &amp; Continue</span>
                    <i class="fas fa-arrow-right cta-arrow"></i>
                  </button>

                  <p class="text-xs text-gray-500 mt-3 text-center">
                    By clicking on Continue, I agree to Vamaship's
                    <a href="https://www.vamaship.com/terms-conditions" class="text-blue-600 hover:underline" target="_blank">Terms &amp; Conditions</a>
                    and
                    <a href="https://www.vamaship.com/privacy-policy" class="text-blue-600 hover:underline" target="_blank">Privacy Policy</a>.
                  </p>

                  <div class="mt-4 text-center">
                    <button @click="changeNumber" class="text-gray-600 hover:text-gray-800 text-sm font-medium underline">
                      Change Phone Number
                    </button>
                  </div>
                </div>

                <!-- Google sign-in -->
                <div v-if="!signupStore.otpSent" class="flex items-center my-4">
                  <div class="flex-1 h-px bg-gray-300"></div>
                  <span class="px-3 text-sm text-gray-500">or</span>
                  <div class="flex-1 h-px bg-gray-300"></div>
                </div>
                <GoogleSignIn
                  v-if="!signupStore.otpSent"
                  :is-loading="authStore.isLoading"
                  @googleSignIn="handleGoogleSignIn"
                  @googleError="handleGoogleError"
                />
              </div>

              <!-- Track tab -->
              <div v-if="activeTab === 'track' && !signupStore.otpSent" class="my-2">
                <TrackingSection />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scroll cue -->
      <button class="scroll-cue" @click="scrollToStory" aria-label="Scroll to explore">
        <span class="scroll-cue-text">Scroll to explore</span>
        <span class="scroll-cue-mouse"><span class="scroll-cue-wheel"></span></span>
      </button>
    </section>

    <!-- ============================ BRANDS ============================ -->
    <section class="brands-band reveal">
      <p class="brands-label">Trusted by thousands of growing brands across India</p>
      <LogoCloud />
    </section>

    <!-- ============================ STATS ============================ -->
    <section class="stats-section reveal">
      <div class="stats-bg" aria-hidden="true">
        <div class="stats-float stats-float-1 plx"><div data-pspeed="-0.2"><i class="fas fa-boxes"></i></div></div>
        <div class="stats-float stats-float-2 plx"><div data-pspeed="0.24"><i class="fas fa-truck-loading"></i></div></div>
        <div class="stats-float stats-float-3 plx"><div data-pspeed="-0.15"><i class="fas fa-route"></i></div></div>
      </div>
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-value"><span class="counter" data-target="10000">0</span>+</div>
          <div class="stat-label">Sellers Onboarded</div>
        </div>
        <div class="stat-item">
          <div class="stat-value"><span class="counter" data-target="27000">0</span>+</div>
          <div class="stat-label">Pincodes Covered</div>
        </div>
        <div class="stat-item">
          <div class="stat-value"><span class="counter" data-target="40">0</span>%</div>
          <div class="stat-label">Shipping Cost Saved</div>
        </div>
        <div class="stat-item">
          <div class="stat-value"><span class="counter" data-target="90">0</span>%</div>
          <div class="stat-label">Delivery Success</div>
        </div>
      </div>
    </section>

    <!-- ============================ FEATURE STORY ============================ -->
    <section ref="storyRef" class="story-section improve-cash-flow-section">
      <div class="story-bg" aria-hidden="true">
        <div class="story-blob story-blob-1 plx"><div data-pspeed="-0.08"></div></div>
        <div class="story-blob story-blob-2 plx"><div data-pspeed="0.1"></div></div>

        <!-- Logistics floaters on deep parallax -->
        <div class="story-float story-float-1 plx"><div data-pspeed="-0.22"><i class="fas fa-box-open"></i></div></div>
        <div class="story-float story-float-2 plx"><div data-pspeed="0.26"><i class="fas fa-dolly"></i></div></div>
        <div class="story-float story-float-3 plx"><div data-pspeed="-0.14"><i class="fas fa-warehouse"></i></div></div>
        <div class="story-float story-float-4 plx"><div data-pspeed="0.18"><i class="fas fa-barcode"></i></div></div>
      </div>

      <!-- Scroll-drawn delivery route -->
      <div class="route-layer" aria-hidden="true">
        <svg ref="routeSvgRef" class="route-svg" viewBox="0 0 1000 3000" preserveAspectRatio="none">
          <defs>
            <linearGradient id="routeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="#8b5cf6" />
              <stop offset="50%" stop-color="#3b82f6" />
              <stop offset="100%" stop-color="#10b981" />
            </linearGradient>
          </defs>
          <path
            class="route-path-base"
            d="M 500 30 C 760 140, 860 260, 830 420 C 800 580, 380 640, 240 820 C 100 1000, 130 1280, 240 1430 C 350 1580, 700 1620, 800 1800 C 900 1980, 860 2200, 760 2360 C 660 2520, 540 2700, 500 2960"
          />
          <path
            ref="routePathRef"
            class="route-path"
            d="M 500 30 C 760 140, 860 260, 830 420 C 800 580, 380 640, 240 820 C 100 1000, 130 1280, 240 1430 C 350 1580, 700 1620, 800 1800 C 900 1980, 860 2200, 760 2360 C 660 2520, 540 2700, 500 2960"
          />
          <circle
            v-for="(cp, i) in routeCheckpoints"
            :key="`cp-${i}`"
            :cx="cp.x"
            :cy="cp.y"
            r="11"
            class="route-stop"
            :class="{ 'route-stop-active': cp.active }"
          />
        </svg>
        <div ref="routeMarkerRef" class="route-marker"><i class="fas fa-truck"></i></div>
      </div>

      <div class="section-container">
        <div class="section-heading reveal">
          <div class="section-badge section-badge-purple">
            <i class="fas fa-star text-yellow-500 mr-2"></i>
            Premium Features
          </div>
          <h2 class="section-h2">
            Improve Cash Flow &amp; Provide a
            <span class="grad-text grad-purple">Premium Buyer Experience</span>
          </h2>
          <p class="section-sub">
            Transform your shipping operations with our cutting-edge solutions designed to boost your business growth
          </p>
        </div>

        <!-- Story row 1: Instant COD -->
        <div class="story-row reveal">
          <div class="story-text">
            <div class="story-num grad-text grad-purple">01</div>
            <div class="story-icon story-icon-purple"><i class="fas fa-money-bill-wave"></i></div>
            <h3 class="story-h3">Instant COD Remittance</h3>
            <p class="story-p">Get COD payments on the same day of delivery. No more waiting for weeks to receive your money.</p>
            <div class="story-tag text-purple-600"><i class="fas fa-clock mr-2"></i> Same Day Processing</div>
          </div>
          <div class="story-media plx">
            <div data-pspeed="-0.12">
              <img src="/images/shopify_page_creatives.png" alt="COD Remittance" class="story-img float-a" />
            </div>
          </div>
        </div>

        <!-- Story row 2: WhatsApp NDR -->
        <div class="story-row story-row-flip reveal">
          <div class="story-text">
            <div class="story-num grad-text grad-green">02</div>
            <div class="story-icon story-icon-green"><i class="fab fa-whatsapp"></i></div>
            <h3 class="story-h3">WhatsApp NDR Services</h3>
            <p class="story-p">Increase your delivery rate to 90% with intelligent WhatsApp integration and automated customer communication.</p>
            <div class="story-tag text-green-600"><i class="fas fa-chart-line mr-2"></i> 90% Delivery Success Rate</div>
          </div>
          <div class="story-media plx">
            <div data-pspeed="0.12" class="flex items-end justify-center gap-4">
              <img src="/images/messaging-mobile.png" alt="WhatsApp Integration" class="story-img-tall float-b" />
              <div class="space-y-3">
                <img src="/images/whatsapp_ndr.png" alt="WhatsApp NDR" class="h-16 sm:h-20 object-contain float-a" />
                <img src="/images/whatsapp_box.png" alt="WhatsApp Box" class="h-24 sm:h-32 object-contain float-c" />
              </div>
            </div>
          </div>
        </div>

        <!-- Story row 3: Tracking page -->
        <div class="story-row reveal">
          <div class="story-text">
            <div class="story-num grad-text grad-blue">03</div>
            <div class="story-icon story-icon-blue"><i class="fas fa-search"></i></div>
            <h3 class="story-h3">Customized Tracking Page</h3>
            <p class="story-p">Offer branded tracking experiences with cross-selling features to maximize customer engagement and revenue.</p>
            <div class="story-tag text-indigo-600"><i class="fas fa-palette mr-2"></i> Fully Branded Experience</div>
          </div>
          <div class="story-media plx">
            <div data-pspeed="-0.1">
              <img src="/images/group_26.png" alt="Customized Tracking" class="story-img-tall float-a" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================ COURIER NETWORK (dark) ============================ -->
    <section class="network-section courier-partners-section">
      <div class="network-bg" aria-hidden="true">
        <div class="aurora aurora-net-1"></div>
        <div class="aurora aurora-net-2"></div>
        <div class="starfield starfield-far"></div>

        <!-- Shipments streaking across the network -->
        <div class="comet comet-1"></div>
        <div class="comet comet-2"></div>
        <div class="comet comet-3"></div>
        <div class="comet comet-4"></div>
      </div>

      <div class="section-container network-grid">
        <div class="network-copy reveal">
          <div class="section-badge section-badge-cyan">
            <i class="fas fa-network-wired mr-2"></i>
            Global Network
          </div>
          <h2 class="section-h2 section-h2-light">
            Access our
            <span class="grad-text grad-cyan">Wide Network</span>
            of Courier Partners
          </h2>
          <p class="section-sub section-sub-light">
            Connect with India's most reliable courier partners through our unified platform.
            Choose from multiple carriers to optimize cost, speed, and reliability.
          </p>
          <div class="network-points">
            <div class="network-point"><span class="net-dot net-dot-green"></span> 99.9% Uptime</div>
            <div class="network-point"><span class="net-dot net-dot-blue"></span> 24/7 Support</div>
            <div class="network-point"><span class="net-dot net-dot-purple"></span> Real-time Tracking</div>
          </div>
        </div>

        <div class="network-visual reveal">
          <div class="orbit-container">
            <div class="orbit-ring orbit-ring-outer"></div>
            <div class="orbit-ring orbit-ring-inner"></div>

            <div class="orbit-center">
              <img src="/images/vamaship-logo.png" alt="Vamaship" class="orbit-center-logo" />
            </div>

            <!-- Inner orbit: capability icons (clockwise) -->
            <div class="inner-orbit">
              <div class="orbit-item orbit-pos-top"><div class="orbit-icon bg-gradient-to-r from-blue-500 to-blue-600"><i class="fas fa-shipping-fast"></i></div></div>
              <div class="orbit-item orbit-pos-tr"><div class="orbit-icon bg-gradient-to-r from-green-500 to-green-600"><i class="fas fa-map-marker-alt"></i></div></div>
              <div class="orbit-item orbit-pos-br"><div class="orbit-icon bg-gradient-to-r from-orange-500 to-orange-600"><i class="fas fa-truck"></i></div></div>
              <div class="orbit-item orbit-pos-bottom"><div class="orbit-icon bg-gradient-to-r from-purple-500 to-purple-600"><i class="fas fa-box"></i></div></div>
              <div class="orbit-item orbit-pos-bl"><div class="orbit-icon bg-gradient-to-r from-yellow-500 to-yellow-600"><i class="fas fa-globe"></i></div></div>
              <div class="orbit-item orbit-pos-tl"><div class="orbit-icon bg-gradient-to-r from-red-500 to-red-600"><i class="fas fa-shield-alt"></i></div></div>
            </div>

            <!-- Outer orbit: courier partner logos (anti-clockwise) -->
            <div class="supplier-orbit">
              <div class="orbit-item orbit-pos-top"><div class="supplier-badge"><img src="/Logistics_Partners_Logos/Bluedart.png" alt="Bluedart" class="supplier-logo" /></div></div>
              <div class="orbit-item orbit-pos-tr"><div class="supplier-badge"><img src="/Logistics_Partners_Logos/Delhivery.png" alt="Delhivery" class="supplier-logo" /></div></div>
              <div class="orbit-item orbit-pos-br"><div class="supplier-badge"><img src="/Logistics_Partners_Logos/EcomExpress.png" alt="Ecom Express" class="supplier-logo" /></div></div>
              <div class="orbit-item orbit-pos-bottom"><div class="supplier-badge"><img src="/Logistics_Partners_Logos/ekart.png" alt="Ekart" class="supplier-logo" /></div></div>
              <div class="orbit-item orbit-pos-bl"><div class="supplier-badge"><img src="/Logistics_Partners_Logos/AmazonShipping.png" alt="Amazon Shipping" class="supplier-logo" /></div></div>
              <div class="orbit-item orbit-pos-tl"><div class="supplier-badge"><img src="/Logistics_Partners_Logos/xpressbees.png" alt="Xpressbees" class="supplier-logo" /></div></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================ STORE INTEGRATION ============================ -->
    <section class="integration-section store-integration-section">
      <div class="story-bg" aria-hidden="true">
        <div class="story-blob story-blob-3 plx"><div data-pspeed="0.08"></div></div>

        <!-- Clouds drifting sideways on scroll -->
        <div class="drift-cloud drift-cloud-1 plx"><div data-pxspeed="0.3"></div></div>
        <div class="drift-cloud drift-cloud-2 plx"><div data-pxspeed="-0.4"></div></div>
        <div class="drift-cloud drift-cloud-3 plx"><div data-pxspeed="0.2"></div></div>
      </div>
      <div class="section-container">
        <div class="section-heading reveal">
          <div class="section-badge section-badge-purple">
            <i class="fas fa-plug mr-2"></i>
            Seamless Integration
          </div>
          <h2 class="section-h2">
            Connect Your Store in
            <span class="grad-text grad-pink">Under 2 Minutes</span>
          </h2>
          <p class="section-sub">
            No technical expertise required. Our one-click integrations work with all major e-commerce platforms.
          </p>
        </div>

        <div class="logo-cloud-container reveal">
          <div class="platform-row">
            <div class="logo-cloud-item platform-chip float-a" style="animation-delay: 0s;"><img src="/images/Shopify.png" alt="Shopify" /></div>
            <div class="logo-cloud-item platform-chip float-b" style="animation-delay: .5s;"><img src="/images/Woocommerce.png" alt="WooCommerce" /></div>
            <div class="logo-cloud-item platform-chip float-c" style="animation-delay: 1s;"><img src="/images/Amazon.png" alt="Amazon" /></div>
          </div>
          <div class="platform-row platform-row-mid">
            <div class="logo-cloud-item platform-chip float-a" style="animation-delay: 1.5s;"><img src="/images/Instamojo.png" alt="Instamojo" /></div>
            <div class="logo-cloud-item platform-chip float-b" style="animation-delay: 2s;"><img src="/images/Unicommerce.png" alt="Unicommerce" /></div>
          </div>
          <div class="platform-row">
            <div class="logo-cloud-item platform-chip float-c" style="animation-delay: 2.5s;"><img src="/images/ZohoCommerce.png" alt="ZohoCommerce" /></div>
            <div class="logo-cloud-item platform-chip float-a" style="animation-delay: 3s;"><img src="/images/Easyecom.png" alt="Easyecom" /></div>
            <div class="logo-cloud-item platform-chip float-b" style="animation-delay: 3.5s;"><img src="/images/Viniculum.png" alt="Viniculum" /></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================ WHY CHOOSE ============================ -->
    <section class="features-section">
      <div class="section-container">
        <div class="section-heading reveal">
          <div class="section-badge section-badge-green">
            <i class="fas fa-star text-yellow-500 mr-2"></i>
            Why Choose Us
          </div>
          <h2 class="section-h2">
            Why Choose
            <span class="grad-text grad-green">Vamaship?</span>
          </h2>
          <p class="section-sub">
            Discover the comprehensive suite of features that make Vamaship the preferred choice for businesses across India
          </p>
        </div>

        <div class="features-grid">
          <div class="feature-card reveal" style="--reveal-delay: 0s">
            <div class="feature-icon feature-icon-blue"><i class="fas fa-shipping-fast"></i></div>
            <h3 class="feature-title">Fast &amp; Reliable Delivery</h3>
            <p class="feature-desc">Get your products to customers quickly with our network of trusted courier partners across India.</p>
            <div class="feature-tag">Most Popular</div>
          </div>

          <div class="feature-card feature-card-hero reveal" style="--reveal-delay: .08s">
            <div class="feature-crown"><i class="fas fa-crown text-yellow-400 mr-2"></i>Most Popular</div>
            <div class="feature-icon feature-icon-purple"><i class="fas fa-money-bill-wave"></i></div>
            <h3 class="feature-title">Instant COD Remittance</h3>
            <p class="feature-desc">Get your COD payments instantly on the same day of delivery. No more waiting weeks for your money.</p>
            <div class="feature-tag">Same Day Payment</div>
          </div>

          <div class="feature-card reveal" style="--reveal-delay: .16s">
            <div class="feature-icon feature-icon-cyan"><i class="fas fa-shield-alt"></i></div>
            <h3 class="feature-title">Valuable Shipping</h3>
            <p class="feature-desc">Protect your valuable shipments with insurance and secure handling protocols.</p>
            <div class="feature-tag">Protected</div>
          </div>

          <div class="feature-card reveal" style="--reveal-delay: .24s">
            <div class="feature-icon feature-icon-green"><i class="fab fa-whatsapp"></i></div>
            <h3 class="feature-title">WhatsApp NDR</h3>
            <p class="feature-desc">Reduce failed deliveries by 90% with smart WhatsApp notifications to customers.</p>
            <div class="feature-tag">90% Success</div>
          </div>

          <div class="feature-card reveal" style="--reveal-delay: .32s">
            <div class="feature-icon feature-icon-orange"><i class="fas fa-clock"></i></div>
            <h3 class="feature-title">Same Day Delivery</h3>
            <p class="feature-desc">Express delivery options for urgent orders in major cities across India.</p>
            <div class="feature-tag">Express</div>
          </div>

          <div class="feature-card reveal" style="--reveal-delay: .4s">
            <div class="feature-icon feature-icon-pink"><i class="fas fa-chart-line"></i></div>
            <h3 class="feature-title">RTO (Return-to-Origin) Reduction</h3>
            <p class="feature-desc">Reduce RTO rates with WhatsApp Order Confirmation &amp; NDR Messaging.</p>
            <div class="feature-tag">RTO Reduction</div>
          </div>
        </div>

        <!-- ============================ TESTIMONIALS ============================ -->
        <div class="testimonials-section reveal">
          <div class="section-heading">
            <div class="section-badge section-badge-orange">
              <i class="fas fa-quote-left text-orange-500 mr-2"></i>
              Customer Stories
            </div>
            <h2 class="section-h2">What Our Customers Say</h2>
            <p class="section-sub">
              Join thousands of satisfied businesses who trust Vamaship for their shipping needs
            </p>
          </div>

          <div class="relative max-w-6xl mx-auto">
            <button
              @click="prevTestimonial"
              @mouseenter="stopCarouselAutoPlay"
              @mouseleave="startCarouselAutoPlay"
              class="carousel-arrow carousel-arrow-left"
              aria-label="Previous testimonial"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            <button
              @click="nextTestimonial"
              @mouseenter="stopCarouselAutoPlay"
              @mouseleave="startCarouselAutoPlay"
              class="carousel-arrow carousel-arrow-right"
              aria-label="Next testimonial"
            >
              <i class="fas fa-chevron-right"></i>
            </button>

            <div ref="carouselContainer" class="overflow-hidden rounded-2xl">
              <div
                class="flex transition-transform duration-500 ease-in-out"
                :style="{ transform: `translateX(-${currentTestimonialIndex * 100}%)` }"
              >
                <div
                  v-for="testimonial in testimonials"
                  :key="testimonial.id"
                  class="w-full flex-shrink-0 px-4"
                >
                  <div class="testimonial-card">
                    <i :class="`fas fa-quote-left ${testimonial.quoteColor} text-2xl mb-4`"></i>
                    <p class="testimonial-quote">"{{ testimonial.quote }}"</p>
                    <div class="testimonial-author">
                      <div :class="`testimonial-avatar bg-gradient-to-r ${testimonial.color}`">
                        {{ testimonial.avatar }}
                      </div>
                      <div>
                        <h4 class="font-semibold text-gray-900 text-lg">{{ testimonial.author }}</h4>
                        <p class="text-sm text-gray-600">{{ testimonial.position }}</p>
                        <div class="flex text-yellow-400 mt-2 text-sm">
                          <i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i><i class="fas fa-star"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex justify-center mt-8 space-x-2">
              <button
                v-for="(testimonial, index) in testimonials"
                :key="`dot-${testimonial.id}`"
                @click="goToTestimonial(index)"
                @mouseenter="stopCarouselAutoPlay"
                @mouseleave="startCarouselAutoPlay"
                :class="[
                  'w-3 h-3 rounded-full transition-all duration-300',
                  currentTestimonialIndex === index ? 'bg-blue-600 scale-125' : 'bg-gray-300 hover:bg-gray-400'
                ]"
                :aria-label="`Go to testimonial ${index + 1}`"
              ></button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============================ FINAL CTA ============================ -->
    <section class="final-cta">
      <div class="final-cta-bg" aria-hidden="true">
        <div class="aurora aurora-cta-1"></div>
        <div class="aurora aurora-cta-2"></div>
        <div class="starfield starfield-near"></div>
      </div>
      <div class="final-cta-inner reveal">
        <h2 class="final-cta-h2">Ready to ship smarter?</h2>
        <p class="final-cta-sub">Create your free seller account and start shipping in minutes.</p>
        <button class="cta-button cta-button-xl" @click="scrollToForm">
          <span>Create your Seller Account</span>
          <i class="fas fa-arrow-up cta-arrow"></i>
        </button>
      </div>
    </section>

    <FloatingHelpButton />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSignupStore } from '../../stores/signup'
import { useAuthStore } from '../../stores/auth'
import TrackingSection from '../../components/signup/TrackingSection.vue'
import GoogleSignIn from '../../components/common/GoogleSignIn.vue'
import OtpInput from '../../components/common/OtpInput.vue'
import SpinnerLoader from '../../components/common/SpinnerLoader.vue'
import LogoCloud from '../../components/common/LogoCloud.vue'
import FloatingHelpButton from '../../components/common/FloatingHelpButton.vue'
import { pushGTMEvent, GTMEvents } from '@/utils/gtm'

const router = useRouter()
const route = useRoute()
const signupStore = useSignupStore()
const authStore = useAuthStore()

const isMobileAlreadyRegistered = ref(false)
const showTrackTooltip = ref(false)
const isTrackTooltipDismissed = ref(false)

// Tab state
const activeTab = ref<'seller' | 'track'>('seller')

// Hero content based on utm_campaign
const heroContent = computed(() => {
  const utmParam = route.query.utm_campaign?.toString()

  const contentMap: Record<string, { h1: string; span: string; image: string }> = {
    flatrate_instcod: {
      h1: 'No RTO Charges. Same-day COD at ₹0.',
      span: 'Ship at flat rates, pay only for forward shipping, and get faster COD settlements.',
      image: '/images/Web_Hero_Img_2.png'
    },
    reduce_rto: {
      h1: 'Reduce RTO by 30% with WhatsApp.',
      span: 'Confirm COD orders before dispatch and resolve delivery failures with WhatsApp NDR alerts.',
      image: '/images/Web_Hero_Img_6.png'
    },
    sdd_ndd: {
      h1: 'Same Day & Next Day Delivery.',
      span: 'Deliver Same Day within metro cities & Next Day across major cities in India.',
      image: '/images/Web_Hero_Img_5.png'
    },
    instant_cod: {
      h1: 'COD Payout in Hours, Not Days.',
      span: 'Receive your COD remittance within hours after delivery and keep your cashflow moving.',
      image: '/images/Web_Hero_Img_4.png'
    },
    no_weight_disputes: {
      h1: 'No More Weight Disputes.',
      span: 'Get charged at flat weight and pay only for what you ship, with no surprise charges.',
      image: '/images/Web_Hero_Img_3.png'
    }
  }

  return contentMap[utmParam || ''] || {
    h1: 'Tired of Overpaying for Shipping?',
    span: 'Cut costs by up to 40% with Vamaship',
    image: '/images/Web_Hero_Img_1.png'
  }
})

// Carousel state
const currentTestimonialIndex = ref(0)
const carouselContainer = ref<HTMLElement | null>(null)
const autoPlayInterval = ref<number | null>(null)

// Testimonials data
const testimonials = ref([
  {
    id: 1,
    quote: "Vamaship has revolutionized our shipping process. The instant COD remittance feature has improved our cash flow significantly.",
    author: "Aman Singh",
    position: "CEO, N-Tech",
    avatar: "R",
    color: "from-blue-500 to-cyan-500",
    quoteColor: "text-blue-500"
  },
  {
    id: 2,
    quote: "The WhatsApp NDR services increased our delivery success rate to 95%. Our customers love the real-time updates.",
    author: "Priya Sharma",
    position: "Founder, StyleHub",
    avatar: "P",
    color: "from-green-500 to-emerald-500",
    quoteColor: "text-green-500"
  },
  {
    id: 3,
    quote: "Integration was seamless. We connected our Shopify store in minutes and started shipping immediately.",
    author: "Aman Patel",
    position: "Owner, GadgetZone",
    avatar: "A",
    color: "from-purple-500 to-indigo-500",
    quoteColor: "text-purple-500"
  },
  {
    id: 4,
    quote: "The analytics dashboard gives us insights we never had before. Our shipping costs have reduced by 30%.",
    author: "Rajesh Kumar",
    position: "Operations Head, TechMart",
    avatar: "R",
    color: "from-orange-500 to-red-500",
    quoteColor: "text-orange-500"
  },
  {
    id: 5,
    quote: "Customer support is outstanding. They helped us optimize our shipping strategy within the first week.",
    author: "Sneha Gupta",
    position: "Founder, FashionForward",
    avatar: "S",
    color: "from-pink-500 to-rose-500",
    quoteColor: "text-pink-500"
  },
  {
    id: 6,
    quote: "The multi-carrier approach ensures our packages always reach on time. Reliability is unmatched.",
    author: "Vikram Singh",
    position: "CEO, ElectronicsHub",
    avatar: "V",
    color: "from-teal-500 to-cyan-500",
    quoteColor: "text-teal-500"
  }
])

// ---------------------------------------------------------------------------
// Parallax / motion engine
// ---------------------------------------------------------------------------
const heroRef = ref<HTMLElement | null>(null)
const storyRef = ref<HTMLElement | null>(null)
const tiltCardRef = ref<HTMLElement | null>(null)
const scrollProgress = ref(0)

// Delivery route (scroll-drawn SVG path with travelling truck)
const routeSvgRef = ref<SVGSVGElement | null>(null)
const routePathRef = ref<SVGPathElement | null>(null)
const routeMarkerRef = ref<HTMLElement | null>(null)
const ROUTE_STOP_FRACTIONS = [0.2, 0.54, 0.88]
const routeCheckpoints = ref<Array<{ x: number; y: number; active: boolean }>>([])
let routeTotalLen = 0

const setupRoute = () => {
  const path = routePathRef.value
  if (!path) return
  routeTotalLen = path.getTotalLength()
  path.style.strokeDasharray = String(routeTotalLen)
  path.style.strokeDashoffset = prefersReducedMotion ? '0' : String(routeTotalLen)
  routeCheckpoints.value = ROUTE_STOP_FRACTIONS.map((f) => {
    const pt = path.getPointAtLength(routeTotalLen * f)
    return { x: pt.x, y: pt.y, active: prefersReducedMotion }
  })
}

const updateRoute = (vh: number) => {
  const svg = routeSvgRef.value
  const path = routePathRef.value
  const marker = routeMarkerRef.value
  const section = storyRef.value
  if (!svg || !path || !marker || !section || !routeTotalLen) return
  if (svg.clientWidth === 0) return // hidden on mobile
  if (prefersReducedMotion) return

  const rect = section.getBoundingClientRect()
  if (rect.bottom < -100 || rect.top > vh + 100) return

  // Truck rides the path, pinned near the viewport's vertical center
  const progress = Math.min(1, Math.max(0, (vh * 0.55 - rect.top) / rect.height))
  path.style.strokeDashoffset = String(routeTotalLen * (1 - progress))

  const pt = path.getPointAtLength(routeTotalLen * progress)
  const sx = svg.clientWidth / 1000
  const sy = svg.clientHeight / 3000
  marker.style.opacity = progress > 0.005 && progress < 0.995 ? '1' : '0'
  marker.style.transform =
    `translate(${(pt.x * sx).toFixed(1)}px, ${(pt.y * sy).toFixed(1)}px) translate(-50%, -50%)`

  routeCheckpoints.value.forEach((cp, i) => {
    cp.active = progress >= ROUTE_STOP_FRACTIONS[i]
  })
}

let prefersReducedMotion = false
let rafPending = false
let mouseX = 0
let mouseY = 0
let motionCleanups: Array<() => void> = []

const applyMotionFrame = () => {
  rafPending = false
  const vh = window.innerHeight

  // Scroll progress bar
  const doc = document.documentElement
  const max = doc.scrollHeight - vh
  scrollProgress.value = max > 0 ? Math.min(1, Math.max(0, doc.scrollTop / max)) : 0

  if (prefersReducedMotion) return

  // Scroll parallax: [data-pspeed] inside a .plx wrapper (wrapper is measured,
  // child is transformed, so the measurement never feeds back into itself)
  document.querySelectorAll<HTMLElement>('[data-pspeed]').forEach((el) => {
    const wrapper = el.closest('.plx') || el.parentElement
    if (!wrapper) return
    const rect = (wrapper as HTMLElement).getBoundingClientRect()
    if (rect.bottom < -200 || rect.top > vh + 200) return
    const speed = parseFloat(el.dataset.pspeed || '0')
    const offset = (rect.top + rect.height / 2 - vh / 2) * speed
    el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`
  })

  // Horizontal scroll parallax: [data-pxspeed] drifts sideways as it crosses the viewport
  document.querySelectorAll<HTMLElement>('[data-pxspeed]').forEach((el) => {
    const wrapper = el.closest('.plx') || el.parentElement
    if (!wrapper) return
    const rect = (wrapper as HTMLElement).getBoundingClientRect()
    if (rect.bottom < -200 || rect.top > vh + 200) return
    const speed = parseFloat(el.dataset.pxspeed || '0')
    const offset = (rect.top + rect.height / 2 - vh / 2) * speed
    el.style.transform = `translate3d(${offset.toFixed(1)}px, 0, 0)`
  })

  // Delivery route draw + truck position
  updateRoute(vh)

  // Mouse parallax in hero: [data-mdepth]
  if (heroRef.value) {
    const heroRect = heroRef.value.getBoundingClientRect()
    if (heroRect.bottom > 0) {
      heroRef.value.querySelectorAll<HTMLElement>('[data-mdepth]').forEach((el) => {
        const depth = parseFloat(el.dataset.mdepth || '0')
        el.style.transform = `translate3d(${(mouseX * depth).toFixed(1)}px, ${(mouseY * depth).toFixed(1)}px, 0)`
      })
    }
  }
}

const requestMotionFrame = () => {
  if (!rafPending) {
    rafPending = true
    requestAnimationFrame(applyMotionFrame)
  }
}

const setupMotion = () => {
  prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  const onScroll = () => requestMotionFrame()
  const onMouseMove = (e: MouseEvent) => {
    mouseX = e.clientX / window.innerWidth - 0.5
    mouseY = e.clientY / window.innerHeight - 0.5
    requestMotionFrame()
  }

  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', onScroll, { passive: true })
  if (!prefersReducedMotion) {
    window.addEventListener('mousemove', onMouseMove, { passive: true })
  }
  motionCleanups.push(() => {
    window.removeEventListener('scroll', onScroll)
    window.removeEventListener('resize', onScroll)
    window.removeEventListener('mousemove', onMouseMove)
  })

  requestMotionFrame()
}

// 3D tilt on the signup card
const handleCardTilt = (e: MouseEvent) => {
  if (prefersReducedMotion) return
  const card = tiltCardRef.value
  if (!card) return
  const rect = card.getBoundingClientRect()
  const px = (e.clientX - rect.left) / rect.width - 0.5
  const py = (e.clientY - rect.top) / rect.height - 0.5
  card.style.transform = `perspective(1100px) rotateY(${(px * 6).toFixed(2)}deg) rotateX(${(-py * 6).toFixed(2)}deg)`
}

const resetCardTilt = () => {
  const card = tiltCardRef.value
  if (card) card.style.transform = 'perspective(1100px) rotateY(0deg) rotateX(0deg)'
}

// Scroll-reveal animations
const setupReveals = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed')
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
  )
  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el))
  motionCleanups.push(() => observer.disconnect())
}

// Counter animation
const setupCounterAnimations = () => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.querySelectorAll('.counter').forEach((counter) => {
            animateCounter(counter as HTMLElement)
          })
          observer.unobserve(entry.target)
        }
      })
    },
    { threshold: 0.5, rootMargin: '0px 0px -100px 0px' }
  )
  const statsSection = document.querySelector('.stats-section')
  if (statsSection) observer.observe(statsSection)
  motionCleanups.push(() => observer.disconnect())
}

const animateCounter = (element: HTMLElement) => {
  const target = parseFloat(element.getAttribute('data-target') || '0')
  const duration = 2000
  const increment = target / (duration / 16)
  let current = 0

  const timer = setInterval(() => {
    current += increment
    if (current >= target) {
      current = target
      clearInterval(timer)
    }
    if (target >= 1000) {
      element.textContent = Math.floor(current).toLocaleString()
    } else if (target % 1 !== 0) {
      element.textContent = current.toFixed(1)
    } else {
      element.textContent = Math.floor(current).toString()
    }
  }, 16)
}

const scrollToStory = () => {
  storyRef.value?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' })
}

const scrollToForm = () => {
  window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------
onMounted(() => {
  // Initialize from session if available
  signupStore.initializeFromSession()

  // Capture UTM parameters and tracking data from route query for attribution
  signupStore.utmSource = route.query.utm_source?.toString() || null
  signupStore.utmMedium = route.query.utm_medium?.toString() || null
  signupStore.utmCampaign = route.query.utm_campaign?.toString() || null
  signupStore.loggedInUsing = route.query.logged_in_using?.toString() || null
  signupStore.googleAds = route.query.google_ads?.toString() || route.query.gclid?.toString() || null

  // Capture invite reference early so it survives the social (Google) round-trip
  const reference = route.query.reference?.toString() || null
  if (reference) {
    signupStore.inviteReference = reference
  }

  setupMotion()
  nextTick(() => {
    setupRoute()
    requestMotionFrame()
  })
  setupReveals()
  setupCounterAnimations()
  startCarouselAutoPlay()
})

onUnmounted(() => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
  }
  motionCleanups.forEach((fn) => fn())
  motionCleanups = []
})

// ---------------------------------------------------------------------------
// Carousel
// ---------------------------------------------------------------------------
const nextTestimonial = () => {
  currentTestimonialIndex.value = (currentTestimonialIndex.value + 1) % testimonials.value.length
}

const prevTestimonial = () => {
  currentTestimonialIndex.value = currentTestimonialIndex.value === 0
    ? testimonials.value.length - 1
    : currentTestimonialIndex.value - 1
}

const goToTestimonial = (index: number) => {
  currentTestimonialIndex.value = index
}

const startCarouselAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
  }
  autoPlayInterval.value = setInterval(() => {
    nextTestimonial()
  }, 5000)
}

const stopCarouselAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
    autoPlayInterval.value = null
  }
}

// ---------------------------------------------------------------------------
// Form / OTP handlers
// ---------------------------------------------------------------------------
const handlePhoneInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value.replace(/[^0-9]/g, '')
  signupStore.formData.phone = value
  isMobileAlreadyRegistered.value = false
  signupStore.clearError('phone')
  if (value.length === 0) {
    isTrackTooltipDismissed.value = false
    showTrackTooltip.value = false
    return
  }
  showTrackTooltip.value = !isTrackTooltipDismissed.value
}

const dismissTrackTooltip = () => {
  isTrackTooltipDismissed.value = true
  showTrackTooltip.value = false
}

const sendOtp = async () => {
  const result = await signupStore.sendMobileOtp()
  // Check if the mobile number already exists (adjust based on actual API response structure)
  if (result && typeof result === 'object' && 'exists' in result && result.exists) {
    isMobileAlreadyRegistered.value = true
  }
}

const verifyOtp = async () => {
  const result = await signupStore.verifyMobileOtp()
  if (result.success) {
    router.push({ path: '/signup/details', query: route.query })
  }
}

const resendOtp = () => {
  signupStore.resendMobileOtp()
}

const goToLogin = () => {
  router.push('/login')
}

const formatOtpTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const handleGoogleSignIn = async (credential: string) => {
  const res = await authStore.socialLoginWithGoogle({
    credential,
    reference: route.query.reference?.toString() ?? null,
    utm_medium: route.query.utm_medium?.toString() ?? null,
    utm_campaign: route.query.utm_campaign?.toString() ?? null,
    utm_source: route.query.utm_source?.toString() ?? null,
  })
  if (res.success) {
    // GTM Event: Google plugin signup (only in signup flow)
    pushGTMEvent(GTMEvents.GOOGLE_PLUGIN_SIGNUP)
  } else {
    console.error(res.error)
  }
}

const handleGoogleError = (message: string) => {
  console.error('Google Sign-In error:', message)
}

const changeNumber = () => {
  // Reset the OTP state
  signupStore.formData.otp = ''
  signupStore.otpSent = false
  signupStore.otpCooldown = 0
  signupStore.clearError('phone')
  signupStore.clearError('otp')
  isMobileAlreadyRegistered.value = false
  signupStore.formData.phone = ''
}
</script>

<style scoped>
/* ============================ Foundation ============================ */
.page-root {
  position: relative;
  width: 100%;
  overflow-x: clip;
  background: #f8fafc;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.scroll-progress {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6A5ACD, #38bdf8, #34d399);
  transform-origin: left;
  transform: scaleX(0);
  z-index: 100;
}

.grad-text {
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}
.grad-purple { background-image: linear-gradient(90deg, #7c3aed, #6366f1, #3b82f6); }
.grad-green  { background-image: linear-gradient(90deg, #059669, #10b981, #14b8a6); }
.grad-blue   { background-image: linear-gradient(90deg, #2563eb, #06b6d4, #6366f1); }
.grad-cyan   { background-image: linear-gradient(90deg, #22d3ee, #60a5fa, #a78bfa); }
.grad-pink   { background-image: linear-gradient(90deg, #9333ea, #db2777, #6366f1); }

/* ============================ Hero ============================ */
.hero {
  position: relative;
  min-height: 100vh;
  background:
    radial-gradient(120% 90% at 80% -10%, #1b2452 0%, transparent 60%),
    radial-gradient(110% 80% at 10% 110%, #14224a 0%, transparent 55%),
    linear-gradient(160deg, #070b1f 0%, #0b1026 45%, #101a3e 100%);
  color: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.55;
  will-change: transform;
  animation: aurora-drift 16s ease-in-out infinite alternate;
}
.aurora-1 {
  width: 55vw; height: 55vw;
  top: -22vw; right: -12vw;
  background: radial-gradient(circle, rgba(106, 90, 205, 0.8), rgba(59, 130, 246, 0.25), transparent 70%);
}
.aurora-2 {
  width: 42vw; height: 42vw;
  bottom: -18vw; left: -14vw;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.5), rgba(99, 102, 241, 0.25), transparent 70%);
  animation-delay: -6s;
}
.aurora-3 {
  width: 30vw; height: 30vw;
  top: 30%; left: 38%;
  background: radial-gradient(circle, rgba(52, 211, 153, 0.32), transparent 70%);
  animation-delay: -11s;
}

@keyframes aurora-drift {
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(3vw, -2vw, 0) scale(1.08); }
  100% { transform: translate3d(-2vw, 2vw, 0) scale(0.96); }
}

.starfield {
  position: absolute;
  inset: -10%;
  background-image:
    radial-gradient(1.5px 1.5px at 12% 28%, rgba(255,255,255,0.9), transparent),
    radial-gradient(1px 1px at 32% 64%, rgba(255,255,255,0.7), transparent),
    radial-gradient(2px 2px at 48% 18%, rgba(255,255,255,0.8), transparent),
    radial-gradient(1px 1px at 64% 76%, rgba(255,255,255,0.6), transparent),
    radial-gradient(1.5px 1.5px at 78% 36%, rgba(255,255,255,0.85), transparent),
    radial-gradient(1px 1px at 88% 84%, rgba(255,255,255,0.6), transparent),
    radial-gradient(1.5px 1.5px at 22% 88%, rgba(255,255,255,0.7), transparent),
    radial-gradient(1px 1px at 56% 50%, rgba(255,255,255,0.55), transparent);
  background-size: 110% 110%;
  animation: star-twinkle 5s ease-in-out infinite alternate;
}
.starfield-far { opacity: 0.45; }
.starfield-near { opacity: 0.8; animation-delay: -2.5s; }

@keyframes star-twinkle {
  from { opacity: 0.35; }
  to   { opacity: 0.85; }
}

.grid-floor {
  position: absolute;
  left: -20%;
  right: -20%;
  bottom: -6%;
  height: 46%;
  background-image:
    linear-gradient(to right, rgba(124, 137, 255, 0.16) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(124, 137, 255, 0.16) 1px, transparent 1px);
  background-size: 56px 56px;
  transform: perspective(620px) rotateX(62deg);
  transform-origin: bottom;
  mask-image: linear-gradient(to top, rgba(0,0,0,0.7), transparent 85%);
  -webkit-mask-image: linear-gradient(to top, rgba(0,0,0,0.7), transparent 85%);
}

.glyph {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 16px;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.85);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.04));
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(6px);
  box-shadow: 0 12px 40px rgba(20, 22, 60, 0.45);
  will-change: transform;
}
.glyph-1 { top: 16%; left: 6%; }
.glyph-2 { top: 64%; left: 3%; width: 44px; height: 44px; font-size: 16px; }
.glyph-3 { top: 12%; right: 38%; width: 46px; height: 46px; font-size: 17px; }
.glyph-4 { bottom: 18%; right: 4%; }
.glyph-5 { top: 42%; right: 44%; width: 40px; height: 40px; font-size: 15px; }

.float-a { animation: float-y 7s ease-in-out infinite; }
.float-b { animation: float-y 9s ease-in-out infinite reverse; }
.float-c { animation: float-y 11s ease-in-out infinite; animation-delay: -3s; }

@keyframes float-y {
  0%, 100% { translate: 0 0; }
  50%      { translate: 0 -16px; }
}

.hero-inner {
  position: relative;
  z-index: 2;
  flex: 1;
  width: 100%;
  max-width: 80rem;
  margin: 0 auto;
  padding: 3rem 1.25rem 5rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  align-items: center;
}

@media (min-width: 1024px) {
  .hero-inner {
    grid-template-columns: 1.1fr 0.9fr;
    padding: 4rem 2rem 6rem;
    gap: 4rem;
  }
}

.hero-logo {
  height: 3.5rem;
  width: auto;
  filter: brightness(0) invert(1);
  margin-bottom: 1.5rem;
}
@media (min-width: 1024px) {
  .hero-logo { height: 4.5rem; }
}

.hero-kicker {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.45rem 1rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #c7d2fe;
  background: rgba(99, 102, 241, 0.14);
  border: 1px solid rgba(129, 140, 248, 0.35);
  margin-bottom: 1.4rem;
}
.kicker-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #34d399;
  box-shadow: 0 0 12px #34d399;
  animation: pulse-dot 2s infinite;
}
@keyframes pulse-dot {
  0%, 100% { transform: scale(1); opacity: 0.85; }
  50%      { transform: scale(1.3); opacity: 1; }
}

.hero-h1 {
  font-size: clamp(2.2rem, 5vw, 3.9rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.02em;
  margin-bottom: 1.1rem;
}
.hero-word {
  display: inline-block;
  background-image: linear-gradient(100deg, #ffffff 0%, #c7d2fe 55%, #67e8f9 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0;
  transform: translateY(28px) rotate(2deg);
  animation: word-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}
@keyframes word-rise {
  to { opacity: 1; transform: translateY(0) rotate(0); }
}

.hero-sub {
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  color: rgba(226, 232, 240, 0.85);
  max-width: 34rem;
  line-height: 1.6;
  margin-bottom: 1.6rem;
}

.hero-image-wrap {
  position: relative;
  max-width: 30rem;
  will-change: transform;
}
.hero-image {
  position: relative;
  z-index: 1;
  width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 30px 50px rgba(10, 12, 40, 0.65));
}
.hero-image-glow {
  position: absolute;
  inset: 12% 6%;
  background: radial-gradient(ellipse at center, rgba(106, 90, 205, 0.45), transparent 70%);
  filter: blur(38px);
  z-index: 0;
}

.hero-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.4rem;
}
.hero-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.83rem;
  font-weight: 600;
  color: #e0e7ff;
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(6px);
}
.hero-chip i { color: #67e8f9; }

/* ---------------- Signup card ---------------- */
.hero-form-col {
  display: flex;
  justify-content: center;
}
@media (min-width: 1024px) {
  .hero-form-col { justify-content: flex-end; }
}

.form-card {
  position: relative;
  width: 100%;
  max-width: 28rem;
  border-radius: 1.5rem;
  transition: transform 0.25s ease-out;
  transform-style: preserve-3d;
  will-change: transform;
}

.form-card-border {
  position: absolute;
  inset: -2px;
  border-radius: 1.6rem;
  background: conic-gradient(from var(--ring-angle, 0deg), #6A5ACD, #38bdf8, #34d399, #6A5ACD);
  animation: ring-spin 7s linear infinite;
  filter: blur(1px);
  opacity: 0.85;
}
@property --ring-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}
@keyframes ring-spin {
  to { --ring-angle: 360deg; }
}

.form-card-body {
  position: relative;
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0.97);
  padding: 2rem;
  box-shadow: 0 30px 80px rgba(7, 11, 31, 0.55);
  min-height: 400px;
}

.form-tabs {
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
}
.form-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}
.form-tab:hover { color: #374151; }
.form-tab-active {
  color: #6A5ACD;
  border-bottom-color: #6A5ACD;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  text-align: center;
  margin-bottom: 0.25rem;
}
.form-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}
.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  min-height: 48px;
  border: 2px solid #d1d5db;
  border-radius: 0.6rem;
  background: #fff;
  color: #111827;
  transition: border-color 0.2s, box-shadow 0.2s;
}
/* Leave room for the +91 | prefix so the placeholder doesn't overlap it */
.form-input--phone {
  padding-left: 4.25rem;
}
.form-input:focus {
  outline: none;
  border-color: #6A5ACD;
  box-shadow: 0 0 0 4px rgba(106, 90, 205, 0.15);
}
.form-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

.cta-button {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 1.4rem;
  border-radius: 0.7rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(120deg, #6A5ACD, #4f46e5 55%, #3b82f6);
  background-size: 180% 100%;
  transition: background-position 0.4s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 10px 26px rgba(79, 70, 229, 0.4);
  overflow: hidden;
}
.cta-button:hover:not(.cta-button-disabled) {
  background-position: 100% 0;
  transform: translateY(-2px);
  box-shadow: 0 14px 32px rgba(79, 70, 229, 0.5);
}
.cta-button .cta-arrow {
  transition: transform 0.25s ease;
  font-size: 0.85em;
}
.cta-button:hover:not(.cta-button-disabled) .cta-arrow {
  transform: translateX(4px);
}
.cta-button-disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}
.cta-button-xl {
  width: auto;
  padding: 1.05rem 2.4rem;
  font-size: 1.1rem;
  border-radius: 999px;
}
.cta-button-xl .cta-arrow { transform: rotate(0); }
.cta-button-xl:hover .cta-arrow { transform: translateY(-3px); }

.link-button {
  color: #2563eb;
  font-size: 0.875rem;
  font-weight: 500;
  text-decoration: underline;
}
.link-button:hover { color: #1e40af; }

.error-box {
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
  color: #b91c1c;
  font-size: 0.875rem;
  font-weight: 500;
}

.otp-input {
  width: 3rem;
  height: 3rem;
  text-align: center;
  border: 2px solid #d1d5db;
  border-radius: 0.6rem;
  background: #fff;
  color: #111827;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}
@media (max-width: 380px) {
  .otp-input { width: 2.5rem; height: 2.5rem; }
}
.otp-input:focus {
  outline: none;
  border-color: #6A5ACD;
  box-shadow: 0 0 0 4px rgba(106, 90, 205, 0.15);
}

.track-tooltip {
  position: absolute;
  top: -0.5rem;
  left: 7rem;
  transform: translateY(-100%);
  background: #111827;
  color: #fff;
  font-size: 0.75rem;
  border-radius: 0.375rem;
  padding: 0.35rem 0.6rem;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  z-index: 10;
}
.track-tooltip-arrow {
  position: absolute;
  left: 1rem;
  top: 100%;
  width: 0.5rem;
  height: 0.5rem;
  background: #111827;
  transform: rotate(45deg) translateY(-50%);
}

/* Cargo plane flyover */
.hero-plane {
  position: absolute;
  top: 16%;
  left: 0;
  display: flex;
  align-items: center;
  color: rgba(199, 210, 254, 0.75);
  font-size: 1.3rem;
  animation: plane-fly 26s linear infinite;
  will-change: transform;
}
.hero-plane-trail {
  width: 7rem;
  height: 0;
  margin-right: 0.6rem;
  border-top: 2px dashed rgba(199, 210, 254, 0.35);
  mask-image: linear-gradient(to right, transparent, #000);
  -webkit-mask-image: linear-gradient(to right, transparent, #000);
}
@keyframes plane-fly {
  0%   { transform: translate3d(-14vw, 3vh, 0); opacity: 0; }
  4%   { opacity: 1; }
  46%  { opacity: 1; }
  50%  { transform: translate3d(114vw, -5vh, 0); opacity: 0; }
  100% { transform: translate3d(114vw, -5vh, 0); opacity: 0; }
}

.scroll-cue {
  position: absolute;
  bottom: 1.4rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: rgba(226, 232, 240, 0.7);
  transition: color 0.2s;
}
.scroll-cue:hover { color: #fff; }
.scroll-cue-text {
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.scroll-cue-mouse {
  width: 26px;
  height: 42px;
  border: 2px solid currentColor;
  border-radius: 999px;
  display: flex;
  justify-content: center;
  padding-top: 7px;
}
.scroll-cue-wheel {
  width: 4px;
  height: 9px;
  border-radius: 999px;
  background: currentColor;
  animation: wheel-drop 1.8s ease-in-out infinite;
}
@keyframes wheel-drop {
  0%   { transform: translateY(0); opacity: 1; }
  70%  { transform: translateY(12px); opacity: 0; }
  100% { transform: translateY(0); opacity: 0; }
}

/* ============================ Shared sections ============================ */
.section-container {
  position: relative;
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1.25rem;
}

.section-heading {
  text-align: center;
  margin-bottom: 4rem;
}
.section-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.2rem;
  border-radius: 999px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}
.section-badge-purple { background: linear-gradient(90deg, #f3e8ff, #dbeafe); color: #6b21a8; }
.section-badge-cyan   { background: rgba(34, 211, 238, 0.12); color: #a5f3fc; border: 1px solid rgba(103, 232, 249, 0.3); }
.section-badge-green  { background: linear-gradient(90deg, #dcfce7, #d1fae5); color: #166534; }
.section-badge-orange { background: linear-gradient(90deg, #fef9c3, #ffedd5); color: #9a3412; }

.section-h2 {
  font-size: clamp(1.9rem, 4vw, 3rem);
  font-weight: 800;
  color: #0f172a;
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin-bottom: 1.25rem;
}
.section-h2-light { color: #f8fafc; }
.section-sub {
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  color: #475569;
  max-width: 46rem;
  margin: 0 auto;
  line-height: 1.65;
}
.section-sub-light { color: rgba(203, 213, 225, 0.85); }

/* Reveal animation */
.reveal {
  opacity: 0;
  transform: translateY(40px);
  transition:
    opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: var(--reveal-delay, 0s);
  will-change: opacity, transform;
}
.reveal.revealed {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal { opacity: 1; transform: none; transition: none; }
  .aurora, .starfield, .float-a, .float-b, .float-c,
  .form-card-border, .scroll-cue-wheel, .hero-word,
  .inner-orbit, .supplier-orbit,
  .hero-plane, .comet, .route-marker::after { animation: none !important; }
  .hero-plane, .comet { display: none; }
  .route-marker { opacity: 0 !important; }
  .hero-word { opacity: 1; transform: none; }
}

/* ============================ Brands band ============================ */
.brands-band {
  position: relative;
  background: linear-gradient(to bottom,
    #101a3e 0%,
    #2c3a66 18%,
    #8b97b8 42%,
    #d8dee9 64%,
    #f8fafc 85%);
  padding: 5rem 0 2.5rem;
}
.brands-label {
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 1.5rem;
}

/* ============================ Stats ============================ */
.stats-section {
  position: relative;
  padding: 3rem 1.25rem 4rem;
  background: #f8fafc;
  overflow: hidden;
}
.stats-bg { position: absolute; inset: 0; pointer-events: none; }
.stats-float { position: absolute; }
.stats-float > div {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 0.9rem;
  font-size: 1.1rem;
  color: #94a3b8;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.08);
  will-change: transform;
}
.stats-float-1 { top: 18%; left: 4%; }
.stats-float-2 { bottom: 12%; right: 5%; }
.stats-float-3 { top: 55%; left: 48%; }
@media (max-width: 1023px) {
  .stats-float { display: none; }
}
.stats-grid { position: relative; z-index: 1; }
.stats-grid {
  max-width: 64rem;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
}
@media (min-width: 768px) {
  .stats-grid { grid-template-columns: repeat(4, 1fr); }
}
.stat-item {
  text-align: center;
  padding: 1.6rem 1rem;
  border-radius: 1.2rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.05);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}
.stat-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.1);
}
.stat-value {
  font-size: clamp(1.8rem, 3.4vw, 2.6rem);
  font-weight: 800;
  background-image: linear-gradient(95deg, #4f46e5, #06b6d4);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.stat-label {
  margin-top: 0.35rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #64748b;
}

/* ============================ Story (features) ============================ */
.story-section {
  position: relative;
  padding: 6rem 0;
  background: linear-gradient(to bottom, #f8fafc, #eef2ff 60%, #f8fafc);
  overflow: hidden;
}
.story-bg { position: absolute; inset: 0; pointer-events: none; }
.story-blob { position: absolute; }
.story-blob > div {
  width: 26rem;
  height: 26rem;
  border-radius: 50%;
  filter: blur(80px);
  will-change: transform;
}
.story-blob-1 { top: 6%; right: -8rem; }
.story-blob-1 > div { background: radial-gradient(circle, rgba(167, 139, 250, 0.35), transparent 70%); }
.story-blob-2 { bottom: 4%; left: -9rem; }
.story-blob-2 > div { background: radial-gradient(circle, rgba(110, 231, 183, 0.3), transparent 70%); }
.story-blob-3 { top: 10%; left: -8rem; }
.story-blob-3 > div { background: radial-gradient(circle, rgba(196, 181, 253, 0.3), transparent 70%); }

/* Logistics floaters (deep parallax) */
.story-float { position: absolute; }
.story-float > div {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 1rem;
  font-size: 1.25rem;
  color: #818cf8;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(165, 180, 252, 0.45);
  backdrop-filter: blur(4px);
  box-shadow: 0 14px 34px rgba(79, 70, 229, 0.14);
  will-change: transform;
}
.story-float-1 { top: 14%; left: 5%; }
.story-float-2 { top: 38%; right: 4%; }
.story-float-3 { top: 64%; left: 7%; }
.story-float-4 { top: 86%; right: 9%; }
@media (max-width: 1023px) {
  .story-float { display: none; }
}

/* Scroll-drawn delivery route */
.route-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  display: none;
  z-index: 0;
}
@media (min-width: 1024px) {
  .route-layer { display: block; }
}
.story-section .section-container {
  position: relative;
  z-index: 1;
}
.route-svg {
  width: 100%;
  height: 100%;
  display: block;
}
.route-path-base {
  fill: none;
  stroke: rgba(99, 102, 241, 0.18);
  stroke-width: 2;
  stroke-dasharray: 2 12;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke;
}
.route-path {
  fill: none;
  stroke: url(#routeGradient);
  stroke-width: 3.5;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke;
  filter: drop-shadow(0 0 7px rgba(99, 102, 241, 0.45));
}
.route-stop {
  fill: #fff;
  stroke: #c7d2fe;
  stroke-width: 3;
  transition: fill 0.4s ease, stroke 0.4s ease;
}
.route-stop-active {
  fill: #6366f1;
  stroke: #a5b4fc;
}
.route-marker {
  position: absolute;
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #fff;
  background: linear-gradient(120deg, #6A5ACD, #3b82f6);
  box-shadow: 0 12px 28px rgba(79, 70, 229, 0.45);
  opacity: 0;
  transition: opacity 0.3s ease;
  will-change: transform;
}
.route-marker::after {
  content: '';
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  border: 2px solid rgba(99, 102, 241, 0.5);
  animation: marker-pulse 1.8s ease-out infinite;
}
@keyframes marker-pulse {
  0%   { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(1.5); opacity: 0; }
}

.story-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
  align-items: center;
  margin-bottom: 6rem;
}
.story-row:last-child { margin-bottom: 0; }
@media (min-width: 1024px) {
  .story-row { grid-template-columns: 1fr 1fr; gap: 4rem; }
  .story-row-flip .story-text { order: 2; }
  .story-row-flip .story-media { order: 1; }
}

.story-text { text-align: center; }
@media (min-width: 1024px) { .story-text { text-align: left; } }

.story-num {
  font-size: 4.2rem;
  font-weight: 900;
  line-height: 1;
  opacity: 0.55;
  margin-bottom: 0.6rem;
}
.story-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 1.2rem;
  font-size: 1.8rem;
  color: #fff;
  margin-bottom: 1.4rem;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.22);
}
.story-icon-purple { background: linear-gradient(120deg, #8b5cf6, #6366f1, #3b82f6); }
.story-icon-green  { background: linear-gradient(120deg, #22c55e, #10b981, #14b8a6); }
.story-icon-blue   { background: linear-gradient(120deg, #6366f1, #3b82f6, #06b6d4); }

.story-h3 {
  font-size: clamp(1.5rem, 2.6vw, 2rem);
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.9rem;
}
.story-p {
  font-size: 1.05rem;
  color: #475569;
  line-height: 1.7;
  max-width: 30rem;
  margin: 0 auto 1.4rem;
}
@media (min-width: 1024px) { .story-p { margin-left: 0; } }
.story-tag {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
}

.story-media { display: flex; justify-content: center; }
.story-media > div { will-change: transform; }
.story-img { height: 13rem; object-fit: contain; filter: drop-shadow(0 24px 40px rgba(15, 23, 42, 0.18)); }
.story-img-tall { height: 16rem; object-fit: contain; filter: drop-shadow(0 24px 40px rgba(15, 23, 42, 0.18)); }
@media (min-width: 640px) {
  .story-img { height: 15rem; }
  .story-img-tall { height: 20rem; }
}

/* ============================ Network (dark) ============================ */
.network-section {
  position: relative;
  padding: 9rem 0 8rem;
  background:
    radial-gradient(90% 70% at 85% 40%, rgba(27, 36, 82, 0.85) 0%, transparent 55%),
    linear-gradient(to bottom,
      #f8fafc 0%,
      #c2cadd 7%,
      #4a567f 16%,
      #0a0f29 28%,
      #0e1736 60%,
      #0a0f29 100%);
  overflow: hidden;
}
.network-bg { position: absolute; inset: 0; pointer-events: none; }
.aurora-net-1 {
  width: 40vw; height: 40vw;
  top: 20%; left: -14vw;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.3), transparent 70%);
}
.aurora-net-2 {
  width: 36vw; height: 36vw;
  bottom: -12vw; right: -8vw;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.4), transparent 70%);
  animation-delay: -7s;
}

/* Shipment comets */
.comet {
  position: absolute;
  width: 150px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, rgba(103, 232, 249, 0.95));
  opacity: 0;
  animation: comet-fly 9s linear infinite;
  will-change: transform, opacity;
}
.comet::after {
  content: '';
  position: absolute;
  right: -3px;
  top: 50%;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  transform: translateY(-50%);
  background: #a5f3fc;
  box-shadow: 0 0 14px 3px rgba(103, 232, 249, 0.8);
}
.comet-1 { top: 18%; left: -5%; transform: rotate(14deg); }
.comet-2 { top: 42%; left: 10%; animation-delay: 2.5s; transform: rotate(10deg); }
.comet-3 { top: 66%; left: -10%; animation-delay: 5s; transform: rotate(18deg); }
.comet-4 { top: 30%; left: 30%; animation-delay: 7s; transform: rotate(8deg); }
@keyframes comet-fly {
  0%   { translate: 0 0; opacity: 0; }
  6%   { opacity: 0.9; }
  28%  { translate: 70vw 16vh; opacity: 0; }
  100% { translate: 70vw 16vh; opacity: 0; }
}

.network-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 3.5rem;
  align-items: center;
}
@media (min-width: 1024px) {
  .network-grid { grid-template-columns: 1fr 1fr; }
}
.network-copy { text-align: center; }
@media (min-width: 1024px) { .network-copy { text-align: left; } }
.network-copy .section-h2,
.network-copy .section-sub { margin-left: 0; margin-right: 0; }
@media (max-width: 1023px) {
  .network-copy .section-sub { margin: 0 auto; }
}

.network-points {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  margin-top: 2rem;
  color: rgba(203, 213, 225, 0.9);
  font-size: 0.9rem;
  font-weight: 500;
}
@media (min-width: 1024px) { .network-points { justify-content: flex-start; } }
.network-point { display: flex; align-items: center; gap: 0.5rem; }
.net-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  animation: pulse-dot 2s infinite;
}
.net-dot-green  { background: #34d399; box-shadow: 0 0 10px #34d399; }
.net-dot-blue   { background: #60a5fa; box-shadow: 0 0 10px #60a5fa; animation-delay: 0.5s; }
.net-dot-purple { background: #a78bfa; box-shadow: 0 0 10px #a78bfa; animation-delay: 1s; }

.network-visual { display: flex; justify-content: center; }

.orbit-container {
  position: relative;
  width: 300px;
  height: 300px;
}
@media (min-width: 640px) {
  .orbit-container { width: 420px; height: 420px; }
}
@media (min-width: 1280px) {
  .orbit-container { width: 500px; height: 500px; }
}

.orbit-ring {
  position: absolute;
  border-radius: 50%;
  border: 2px dashed rgba(103, 232, 249, 0.35);
}
.orbit-ring-outer { inset: 0; }
.orbit-ring-inner { inset: 18%; border-color: rgba(167, 139, 250, 0.35); }

.orbit-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  width: 6rem;
  height: 6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(120deg, #2563eb, #06b6d4);
  box-shadow: 0 0 50px rgba(56, 189, 248, 0.5);
}
@media (min-width: 640px) {
  .orbit-center { width: 7.5rem; height: 7.5rem; }
}
.orbit-center-logo {
  width: 70%;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

.inner-orbit {
  position: absolute;
  inset: 18%;
  animation: orbit-spin 20s linear infinite;
}
.supplier-orbit {
  position: absolute;
  inset: 0;
  animation: orbit-spin 25s linear infinite reverse;
}
@keyframes orbit-spin {
  to { transform: rotate(360deg); }
}

/* Counter-rotate children so they stay upright */
.inner-orbit .orbit-item > * { animation: orbit-spin 20s linear infinite reverse; }
.supplier-orbit .orbit-item > * { animation: orbit-spin 25s linear infinite; }

.orbit-item { position: absolute; }
.orbit-pos-top    { top: 0; left: 50%; transform: translate(-50%, -50%); }
.orbit-pos-tr     { top: 25%; right: 0; transform: translate(50%, -50%); }
.orbit-pos-br     { bottom: 25%; right: 0; transform: translate(50%, 50%); }
.orbit-pos-bottom { bottom: 0; left: 50%; transform: translate(-50%, 50%); }
.orbit-pos-bl     { bottom: 25%; left: 0; transform: translate(-50%, 50%); }
.orbit-pos-tl     { top: 25%; left: 0; transform: translate(-50%, -50%); }

.orbit-icon {
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1rem;
  box-shadow: 0 10px 24px rgba(7, 11, 31, 0.5);
}
@media (min-width: 640px) {
  .orbit-icon { width: 3rem; height: 3rem; font-size: 1.15rem; }
}

.supplier-badge {
  background: rgba(255, 255, 255, 0.96);
  border-radius: 999px;
  padding: 0.45rem 0.9rem;
  box-shadow: 0 10px 26px rgba(7, 11, 31, 0.45);
}
.supplier-logo {
  height: 1.4rem;
  width: auto;
  object-fit: contain;
}
@media (min-width: 640px) {
  .supplier-logo { height: 1.8rem; }
}

/* ============================ Integration ============================ */
.integration-section {
  position: relative;
  padding: 7rem 0 6rem;
  background: linear-gradient(to bottom,
    #0a0f29 0%,
    #2c3a66 10%,
    #8b97b8 24%,
    #d8dee9 38%,
    #f8fafc 52%);
  overflow: hidden;
}
.integration-section .section-heading { padding-top: 4rem; }

/* Drifting clouds (horizontal parallax) */
.drift-cloud { position: absolute; }
.drift-cloud > div {
  width: 16rem;
  height: 5.5rem;
  border-radius: 999px;
  background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.95), rgba(241, 245, 249, 0.4) 70%, transparent);
  filter: blur(10px);
  will-change: transform;
}
.drift-cloud-1 { top: 22%; left: 6%; }
.drift-cloud-2 { top: 48%; right: 4%; }
.drift-cloud-3 { bottom: 14%; left: 18%; }
@media (max-width: 1023px) {
  .drift-cloud { display: none; }
}

.logo-cloud-container { position: relative; padding: 1rem 0; }
.platform-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2.5rem;
  margin-bottom: 1.4rem;
}
@media (min-width: 640px) { .platform-row { gap: 4.5rem; } }
.platform-row-mid { gap: 4rem; }
@media (min-width: 640px) { .platform-row-mid { gap: 7rem; } }

.platform-chip {
  padding: 0.9rem 1.4rem;
  border-radius: 1.1rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.platform-chip:hover {
  transform: translateY(-6px) scale(1.05);
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.14);
}
.platform-chip img {
  height: 2.6rem;
  width: auto;
  object-fit: contain;
}
@media (min-width: 1024px) { .platform-chip img { height: 3.4rem; } }

/* ============================ Features grid ============================ */
.features-section {
  position: relative;
  padding: 6rem 0;
  background: linear-gradient(to bottom, #f8fafc, #eef2ff 50%, #f8fafc);
}
.features-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
@media (min-width: 640px)  { .features-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .features-grid { grid-template-columns: repeat(3, 1fr); } }

.feature-card {
  position: relative;
  padding: 2rem;
  border-radius: 1.4rem;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(226, 232, 240, 0.9);
  backdrop-filter: blur(8px);
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.06);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  overflow: hidden;
}
.feature-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(420px circle at 50% -10%, rgba(99, 102, 241, 0.1), transparent 60%);
  opacity: 0;
  transition: opacity 0.4s ease;
}
.feature-card:hover {
  transform: translateY(-8px);
  border-color: rgba(129, 140, 248, 0.5);
  box-shadow: 0 26px 60px rgba(79, 70, 229, 0.16);
}
.feature-card:hover::before { opacity: 1; }

.feature-card-hero {
  background: linear-gradient(150deg, #ffffff, #eef2ff);
  border-color: rgba(129, 140, 248, 0.5);
}
.feature-crown {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6d28d9;
  margin-bottom: 1rem;
}

.feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3.6rem;
  height: 3.6rem;
  border-radius: 1rem;
  color: #fff;
  font-size: 1.4rem;
  margin-bottom: 1.2rem;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.18);
  transition: transform 0.3s ease;
}
.feature-card:hover .feature-icon {
  transform: scale(1.1) rotate(-4deg);
}
.feature-icon-blue   { background: linear-gradient(120deg, #3b82f6, #06b6d4); }
.feature-icon-purple { background: linear-gradient(120deg, #8b5cf6, #6366f1); }
.feature-icon-cyan   { background: linear-gradient(120deg, #06b6d4, #0ea5e9); }
.feature-icon-green  { background: linear-gradient(120deg, #22c55e, #10b981); }
.feature-icon-orange { background: linear-gradient(120deg, #f97316, #f59e0b); }
.feature-icon-pink   { background: linear-gradient(120deg, #ec4899, #f43f5e); }

.feature-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.6rem;
}
.feature-desc {
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.65;
  margin-bottom: 1.1rem;
}
.feature-tag {
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4f46e5;
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.25);
  border-radius: 999px;
  padding: 0.3rem 0.8rem;
}

/* ============================ Testimonials ============================ */
.testimonials-section { margin-top: 6rem; }

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 3.4rem;
  height: 3.4rem;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.14);
  transition: transform 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
}
.carousel-arrow:hover {
  transform: translateY(-50%) scale(1.12);
  color: #4f46e5;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.2);
}
.carousel-arrow-left  { left: 0; margin-left: -1rem; }
.carousel-arrow-right { right: 0; margin-right: -1rem; }

.testimonial-card {
  max-width: 56rem;
  margin: 0 auto;
  padding: 2.6rem;
  border-radius: 1.6rem;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(226, 232, 240, 0.9);
  backdrop-filter: blur(8px);
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.08);
}
.testimonial-quote {
  color: #334155;
  line-height: 1.75;
  font-size: 1.1rem;
  margin-bottom: 1.6rem;
}
.testimonial-author {
  display: flex;
  align-items: center;
  gap: 1.1rem;
}
.testimonial-avatar {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 1.3rem;
  flex-shrink: 0;
}

/* ============================ Final CTA ============================ */
.final-cta {
  position: relative;
  padding: 10rem 1.25rem 8rem;
  background:
    radial-gradient(100% 80% at 50% 120%, rgba(27, 36, 82, 0.85) 0%, transparent 60%),
    linear-gradient(to bottom,
      #f8fafc 0%,
      #c2cadd 8%,
      #4a567f 18%,
      #0a0f29 32%,
      #101a3e 100%);
  overflow: hidden;
  text-align: center;
}
.final-cta-bg { position: absolute; inset: 0; pointer-events: none; }
.final-cta-bg .starfield { top: 35%; }
.aurora-cta-1 {
  width: 44vw; height: 44vw;
  top: 30%; left: 10vw;
  background: radial-gradient(circle, rgba(106, 90, 205, 0.5), transparent 70%);
}
.aurora-cta-2 {
  width: 36vw; height: 36vw;
  bottom: -14vw; right: 4vw;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.4), transparent 70%);
  animation-delay: -8s;
}
.final-cta-inner {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.1rem;
}
.final-cta-h2 {
  font-size: clamp(2rem, 4.6vw, 3.4rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  background-image: linear-gradient(100deg, #ffffff, #c7d2fe 55%, #67e8f9);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.final-cta-sub {
  font-size: 1.1rem;
  color: rgba(203, 213, 225, 0.85);
  margin-bottom: 0.8rem;
}
</style>
